import { Component, EventEmitter, forwardRef, inject, Input, Output, Type } from '@angular/core';
import {
  AbstractControl,
  ControlContainer,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  ValidationErrors,
  Validator,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { FormFieldConfigType } from '../models';
import { FormValidators } from '../validators';

export const baseProviderComponent = (comp: Type<NonNullable<unknown>>) => [
  {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => comp),
    multi: true,
  },
  {
    provide: NG_VALIDATORS,
    useExisting: forwardRef(() => comp),
    multi: true,
  },
];

@Component({
  selector: 'ui-form-base-field',
  template: '',
  standalone: true,
})
export class BaseFormFieldComponent<T = string> implements ControlValueAccessor, Validator {
  @Input() set config(configValue: FormFieldConfigType) {
    this.label = configValue.label;
    this.placeholder = configValue.placeholder ?? '';
    this.hideLabel = configValue.hideLabel ?? false;
    this.loading = configValue.loading ?? false;
  }
  @Input() fieldName?: string;
  @Input() control?: AbstractControl;

  @Input() label?: string;
  @Input() placeholder = '';
  @Input() required = false;
  @Input() disabled = false;
  @Input() hideLabel = false;
  @Input() hideError = false;
  @Input() value?: T;
  @Input() loading?: boolean;
  @Input() isUpperCase?: boolean;

  @Output() enterPressed = new EventEmitter<void>();
  @Output() valueChange = new EventEmitter<T>();

  protected controlContainer = inject(ControlContainer, { optional: true });

  get parentFormGroup(): AbstractControl | undefined {
    return this.controlContainer?.control as AbstractControl;
  }

  get currentFormControl() {
    return this.fieldName ? this.parentFormGroup?.get(this.fieldName) : undefined;
  }

  errors?: ValidationErrors | null;

  get errorTip(): string {
    // Check if there are any errors, return early if none.
    if (!this.control?.errors) {
      return '';
    }

    // Directly access the first error key and value.
    const [, firstErrorValue] = Object.entries(this.control.errors)[0];

    // If the error value is a string, return it, otherwise use a default message.
    return typeof firstErrorValue === 'string'
      ? firstErrorValue
      : typeof firstErrorValue === 'object'
      ? firstErrorValue?.translation
      : 'form.error.required';
  }

  get isRequired(): boolean {
    if (this.control == null) {
      return this.required;
    }
    return (
      this.control.hasValidator(FormValidators.required) ||
      this.control.hasValidator(FormValidators.translatableFieldRequiredValidator as ValidatorFn) ||
      this.control.hasValidator(Validators.required)
    );
  }

  protected onEnterPressed(): void {
    this.enterPressed.emit();
  }

  protected onValidatorChange: () => void = () => void {};
  protected onChange: (value: T) => void = () => void {};
  protected onTouched: () => void = () => void {};

  // ControlValueAccessor methods
  writeValue(value: T): void {
    this.value = value;
    this.updateControlValidity(); // Ensure control state is updated
  }

  registerOnChange(fn: (value: T) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  // Validator method
  validate(control: AbstractControl): ValidationErrors | null {
    this.control = control; // Keep a reference to the control
    return control.errors; // Return the errors from the control itself
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn; // Store the callback function
  }

  protected updateControlValidity(): void {
    if (this.control) {
      this.control.setErrors(null);
      this.control.updateValueAndValidity({ emitEvent: false });
    }
    // Notify Angular about the validator change
    this.onValidatorChange();
  }
}
